import { supabase } from "@/integrations/supabase/client";

/**
 * Audit Logger Utility
 * Tracks admin access to sensitive user data for security compliance
 */

export type AccessType = 'view' | 'update' | 'delete' | 'bulk_view' | 'export';

interface AuditLogParams {
  profileId: string;
  accessType: AccessType;
  adminId?: string;
}

/**
 * Log profile access for audit trail via edge function
 * Fails gracefully - never blocks admin operations
 */
export async function logProfileAccess({ 
  profileId, 
  accessType,
}: Omit<AuditLogParams, 'adminId'>): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('log-profile-access', {
      body: {
        profileIds: [profileId],
        accessType,
      },
    });

    if (error) {
      console.error('[AUDIT] Failed to log access:', error.message);
    }
  } catch (error) {
    // Never throw - logging failures shouldn't break admin operations
    console.error('[AUDIT] Unexpected error:', error);
  }
}

/**
 * Log bulk profile view (e.g., subscriber list) via edge function
 */
export async function logBulkProfileView(profileIds: string[]): Promise<void> {
  try {
    if (profileIds.length === 0) return;

    const { error } = await supabase.functions.invoke('log-profile-access', {
      body: {
        profileIds,
        accessType: 'bulk_view' as AccessType,
      },
    });

    if (error) {
      console.error('[AUDIT] Failed to log bulk view:', error.message);
    }
  } catch (error) {
    console.error('[AUDIT] Bulk logging error:', error);
  }
}
